<template>
  <div class="form-container">
    <h1 class="page-title">臨時作業情報編集</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
      <!-- 請求書No. -->
      <div class="mb-3">
        <label for="quotionNo" class="form-label">請求ID：{{ temporaryWork.billing_no }}</label>      
      </div>

      <!-- 見積No. -->
      <div class="mb-3">
        <label for="quotionNo" class="form-label">見積No.</label>
        <input v-model="temporaryWork.quotion_no" type="text" placeholder="見積No." class="form-control w-third-forth" id="quotionNo">
        <div v-if="errorMessages.quotion_no" class="alert alert-danger mt-2">{{ errorMessages.quotion_no[0] }}</div>
      </div>

      <!-- 建物名 -->
      <div class="mb-3">
        <label for="quotionNo" class="form-label">建物名 <span class="badge text-bg-danger">必須</span></label>
        <BuildingSelectBox
          v-model="temporaryWork.m_building_id"
          :options="buildings"
          :required="true"
        />
      </div>

      <!-- 業務区分 -->
      <div class="mb-3">
        <label for="businessClassification" class="form-label">区分 <span class="badge text-bg-danger">必須</span></label>
        <select id="businessClassification" class="form-select w-third" v-model="temporaryWork.m_business_classification_id" required>
          <option value="" selected>選択してください</option> <!-- placeholder -->
          <option v-for="businessClassification in businessClassifications" :key="businessClassification.id" :value="businessClassification.id">{{ businessClassification.business_classification_nm }}</option>
        </select>
        <div v-if="errorMessages.m_business_classification_id" class="alert alert-danger mt-2">{{ errorMessages.m_business_classification_id[0] }}</div>
      </div>

      <!-- 業務内容 -->
      <div class="mb-3">
        <label for="workContent" class="form-label">
          業務内容 <span class="badge text-bg-danger">必須</span>
        </label>
        <textarea
          v-model="temporaryWork.work_content"
          id="workContent"
          class="form-control w-third-forth"
          placeholder="業務内容"
          rows="2"
          required
          @keydown.enter.stop
        ></textarea>
        <span class="text-danger">※請求書の摘要に反映されます</span>
        <div
          v-if="errorMessages.work_content"
          class="alert alert-danger mt-2"
        >
          {{ errorMessages.work_content[0] }}
        </div>
      </div>

      <!-- 内訳 -->
      <div class="mb-3">
        <label for="breakdown" class="form-label">内訳</label>
        <textarea
          v-model="temporaryWork.breakdown"
          id="breakdown"
          class="form-control w-third-forth"
          placeholder="内訳・詳細説明など"
          rows="2"
          @keydown.enter.stop
        ></textarea>
        <span class="text-danger">※請求書の内訳に反映されます</span>
        <div
          v-if="errorMessages.breakdown"
          class="alert alert-danger mt-2"
        >
          {{ errorMessages.breakdown[0] }}
        </div>
      </div>

      <!-- 顧客名(請求先) -->
      <div class="mb-3">
        <label for="building" class="form-label">請求先 <span class="badge text-bg-danger">必須</span></label>
        <CustomerSelectBox
          v-model="temporaryWork.m_customer_id"
          :options="customers"
          :required="true"
        />
      </div>

      <!-- 受注日 -->
      <div class="mb-3">
        <label for="orderDt" class="form-label">受注日 <span class="badge text-bg-danger">必須</span></label>
        <input
          v-model="temporaryWork.order_dt"
          type="date"
          class="form-control w-third"
          id="orderDt"
          max="2099-12-31"
          required
        >
      </div>

      <!-- 受注年と受注月 -->
      <input v-model="temporaryWork.order_year" type="hidden">
      <input v-model="temporaryWork.order_month" type="hidden">

      <!-- 受注金額(税抜) -->
      <div class="mb-3">
        <label for="orderAmount" class="form-label">受注金額(税抜)</label>
        <div class="d-flex align-items-center">
          <CommaNumberInput
            v-model="temporaryWork.order_amount"
            placeholder="受注金額(税抜)"
            class="form-control w-third-forth"
          />
        </div>
      </div>

      <!-- 自社作業チェック -->
      <div class="mb-3">
        <label for="is_in_house" class="ECM_CheckboxInput">
          <input
            v-model="temporaryWork.is_in_house"
            :true-value="true"
            :false-value="false"
            id="is_in_house"
            type="checkbox"
            class="ECM_CheckboxInput-Input"
          >
          <span class="ECM_CheckboxInput-DummyInput"></span>
          <h4 class="ms-3">自社で作業</h4>
        </label>
      </div>

      <!-- 発注業者 -->
      <div v-if="!temporaryWork.is_in_house">
        <label for="vendor" class="form-label">発注業者情報</label>
        <div>
          <table class="table table-bordered monthly-detail-data">
            <thead>
              <tr>
                <th class="text-nowrap month-data-header">発注業者</th>
                <th class="text-nowrap month-data-header">依頼金額</th>
                <th class="month-data-header"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(vendor, index) in selectedVendors" :key="index">
                <!-- 並び順 -->
                <td v-show="false"><input v-model="vendor.sort_no" type="number" step="0.1" placeholder="並び順" class="form-control"></td>
                <td class="align-top">
                  <VendorSelectBox
                    v-model="vendor.m_vendor_id"
                    :options="vendors"
                    :required="!temporaryWork.is_in_house"
                  />
                </td>
                <td class="align-top">
                  <div class="d-flex align-items-center">
                    <CommaNumberInput
                      v-model="vendor.request_amount"
                      placeholder="依頼金額"
                      class="form-control ms-2"
                    />
                  </div>
                </td>
                <!-- 削除ボタン -->
                <td class="btn-width align-middle">
                  <div>
                    <button type="button" @click="removeVendor(index)" class="btn btn-sm btn-danger ms-2 text-nowrap">削除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
  
          <!-- 発注業者を追加するボタン -->
          <div class="text-center">
            <button type="button" @click="addVendor" class="btn btn-sm btn-info mt-2">発注業者を追加</button>
          </div>
        </div>
      </div>

      <!-- 作業日 -->
      <div class="mb-3">
        <label for="workDt" class="form-label">作業日</label>
        <input
          v-model="temporaryWork.work_dt"
          type="date"
          class="form-control w-third"
          id="workDt"
          max="2099-12-31"
        >
      </div>

      <!-- 当社担当者 -->
      <div class="mb-3">
        <label for="companyRepresentative" class="form-label">当社担当者</label>
        <select class="form-select w-third" v-model="temporaryWork.user_id">
          <option value="">選択してください</option>
          <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
        </select>
      </div>
      
      <!-- 請求年月 -->
      <div class="mb-3">
        <label class="form-label">請求年月 <span class="badge text-bg-danger">必須</span></label>
        <div class="d-flex align-items-center gap-2">
          <select class="form-select w-100" v-model.number="temporaryWork.billing_year" required>
            <option value="" disabled>年を選択</option>
            <option v-for="y in years" :key="`y-${y}`" :value="y">{{ y }}</option>
          </select>
          <span class="mx-2">年</span>
          <select class="form-select w-80" v-model.number="temporaryWork.billing_month" required>
            <option value="" disabled>月を選択</option>
            <option v-for="m in months" :key="`m-${m}`" :value="m">{{ m }}</option>
          </select>
          <span class="mx-2">月</span>
        </div>
        <div v-if="errorMessages.billing_year" class="alert alert-danger mt-2">{{ errorMessages.billing_year[0] }}</div>
        <div v-if="errorMessages.billing_month" class="alert alert-danger mt-2">{{ errorMessages.billing_month[0] }}</div>
      </div>

      <div class="btn-area">
        <a
          class="btn btn-secondary mx-1"
          @click="
            router.push({
              name: 'TemporaryWorkList',
              query: route.query
            })
          "
        >
          戻る
        </a>
        <button type="submit" class="btn btn-warning mx-1">更新</button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, onMounted, computed, watchEffect, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  getTemporaryWork,
  getSelectBoxUsers,
  getSelectBoxBuildings,
  getBusinessClassifications,
  getSelectBoxCustomers,
  getSelectBoxVendors, updateTemporaryWork
} from '@/api/axios';
import BuildingSelectBox from '@/components/searchSelectBoxes/BuildingSelectBox.vue';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import VendorSelectBox from '@/components/searchSelectBoxes/VendorSelectBox.vue';
import CommaNumberInput from '@/components/shared/CommaNumberInput.vue';

export default {
  components: {
    BuildingSelectBox,
    CustomerSelectBox,
    VendorSelectBox,
    CommaNumberInput
  },
  setup() {
    const title = ref('臨時作業情報編集');

    watchEffect(() => {
      document.title = title.value;
    });

    const userId = ref('');
    const userName = ref('');
    const users = ref([]);
    const buildings = ref([]);
    const businessClassifications = ref([]);
    const customers = ref([]);
    const vendors = ref([]);
    const selectedVendors = ref([]);
    const temporaryWork = ref({
      billing_no: '',
      quotion_no: '',
      m_bulding_id: '',
      m_customer_id: '',
      work_content: '',
      breakdown: '',
      m_customer_representative_id: '',
      order_year: '',
      order_month: '',
      order_dt: '',
      order_amount: '',
      t_temporary_work_vendors: [],
      confirm_vendor_biling: '',
      vendor_billing_confirm_dt: '',
      gross_profit_margin: '',
      work_dt: '',
      user_id: '',
      billing_request: '',
      billing_year: '',
      billing_month: '',
      confirm_billing: '',
      confirm_billing_dt: '',
      billing_count: '',
      is_in_house: false,
      // 他のフィールド
    });

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});
    const vendorInputError = ref(false);

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const temporaryWorkId = route.params.id; // URLから臨時作業情報IDを取得

    // ▼▼ 共通：検索条件をそのまま query として返す ▼▼
    const getSearchQuery = () => {
      const q = route.query;
      return {
        temp_billing_no: q.temp_billing_no || '',
        temp_year_from: q.temp_year_from || '',
        temp_month_from: q.temp_month_from || '',
        temp_year_to: q.temp_year_to || '',
        temp_month_to: q.temp_month_to || '',
        temp_customer_id: q.temp_customer_id || '',
        temp_building_id: q.temp_building_id || '',
        temp_vendor_id: q.temp_vendor_id || '',
        temp_user_id: q.temp_user_id || '',
        temp_work_content: q.temp_work_content || '',
        temp_billing_flg: q.temp_billing_flg || '',
        temp_order_sort_order: q.temp_order_sort_order || 'asc',
        temp_work_sort_order: q.temp_work_sort_order || '',
        temp_page: q.temp_page || 1
      };
    };

    // 年・月のセレクト用
    const years = ref([]);
    const months = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

    // 今日の年月で初期化 & 年リスト（前後5年）生成
    (() => {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1; // 0始まりなので +1

      for (let y = currentYear - 5; y <= currentYear + 5; y++) {
        years.value.push(y);
      }

      // 既に値が入っていないときのみデフォルト設定
      if (!temporaryWork.value.billing_year) {
        temporaryWork.value.billing_year = currentYear;
      }
      if (!temporaryWork.value.billing_month) {
        temporaryWork.value.billing_month = currentMonth;
      }
    })();

    const fetchSelectBoxItems = () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');

      Promise.all([
        getSelectBoxUsers(),
        getSelectBoxBuildings(),
        getBusinessClassifications(),
        getSelectBoxCustomers(),
        getSelectBoxVendors()
      ])
        .then(([usersRes, buildingsRes, businessClassificationsRes, customersRes, vendorsRes]) => {
          users.value = usersRes.data;
          buildings.value = buildingsRes.data;
          businessClassifications.value = businessClassificationsRes.data;
          customers.value = customersRes.data;
          vendors.value = vendorsRes.data;

          // 🔸 options をセットした後に temporaryWork を取得
          return getTemporaryWork(temporaryWorkId);
        })
        .then((tempRes) => {
          temporaryWork.value = tempRes.data;
          selectedVendors.value = tempRes.data.t_temporary_work_vendors || [];

          // 🔸 自社作業でない & 業者情報がない場合に空行を追加
          if (
            !temporaryWork.value.is_in_house &&
            selectedVendors.value.length === 0
          ) {
            selectedVendors.value.push({
              m_vendor_id: null,
              request_amount: '',
              sort_no: '1'
            });
          }
        })
        .catch((error) => {
          console.error('選択肢または臨時作業情報の取得に失敗しました:', error);
        });
    };

    const handleSubmit = async () => {
      vendorInputError.value = false;

      // バリデーション: 自社作業でないのに業者がいない
      if (!temporaryWork.value.is_in_house) {
        const hasValidVendors = selectedVendors.value.some(v => v.m_vendor_id);
        if (!hasValidVendors) {
          vendorInputError.value = true;
          store.dispatch('setFlashMessage', {
            message: '発注業者を1件以上選択してください',
            type: 'danger'
          });
          return;
        }
      }
      try {
        temporaryWork.value.t_temporary_work_vendors = selectedVendors.value;

        await updateTemporaryWork(temporaryWorkId, temporaryWork.value);
        store.dispatch('setFlashMessage', {message: '臨時作業情報更新完了！', type: 'success'});
        router.push({
          name: 'TemporaryWorkDetail',
          params: { id: temporaryWorkId },
          query: getSearchQuery()
        });
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
        }

        store.dispatch('setFlashMessage', {message: '臨時作業情報更新失敗！', type: 'danger'});
        console.error('作成エラー:', error);
      }
    };
    
    // 発注業者を追加するメソッド
    const addVendor = () => {
      selectedVendors.value.push({ m_vendor_id: '', request_amount: '', sort_no: '' });
    };

    // 発注業者を削除するメソッド
    const removeVendor = (index) => {
      selectedVendors.value.splice(index, 1);
    };

    // 発注業者の数を監視し、変更があるたびにsort_noを更新
    watch(
      () => selectedVendors.value.length,
      () => {
        // 発注業者の並び順のナンバリングを更新
        selectedVendors.value.forEach((vendor, index) => {
          vendor.sort_no = (index + 1).toString(); // sort_noを更新
        });
      }
    );
    
    // 受注日から受注年と受注月を計算する
    useDateWatcher(
      computed(() => temporaryWork.value.order_dt),
      temporaryWork,
      'order'
    );

    // 日付入力をすると、年と月の値を計算する関数
    function useDateWatcher(sourceRef, targetRef, dateFieldPrefix) {
      watch(sourceRef, (newVal) => {
        if (newVal) {
          const date = new Date(newVal);
          targetRef.value[`${dateFieldPrefix}_year`] = date.getFullYear();
          targetRef.value[`${dateFieldPrefix}_month`] = date.getMonth() + 1;
        } else {
          targetRef.value[`${dateFieldPrefix}_year`] = '';
          targetRef.value[`${dateFieldPrefix}_month`] = '';
        }
      });
    }

    onMounted(fetchSelectBoxItems);

    return {
      title,
      users,
      buildings,
      businessClassifications,
      customers,
      vendors,
      years,
      months,
      router,
      route,
      getSearchQuery,
      temporaryWork,
      selectedVendors,
      addVendor,
      removeVendor,
      handleSubmit,
      errorMessages,
    };
  }
};
</script>

<style scoped>
.form-container {
  width: 70%;
  padding: 20px 20px 80px 20px;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

input[type="text"],
input[type="email"],
input[type="password"] {
  width: 100%;
  padding: 10px;
  margin-bottom: 4px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.monthly-detail-data{
  margin: 0 auto 20px 0;
  width: 100%;
  font-size: 14px;
}
.monthly-detail-data .month-data-header{
  background-color: #003366;
  color: #fff;
}
.monthly-detail-data .month-data-left{
  background-color: #f0f8ff;
}
.monthly-detail-data .month-width{
  width: 120px;
}
.monthly-detail-data input[type="number"],
.monthly-detail-data select,
.monthly-detail-data textarea{
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}
.monthly-detail-data .btn-width{
  width: 80px;
}
.btn-area{
  text-align: center;
}

.w-half{
  width: 50% !important;
}

.w-third{
  width: 30% !important;
}

.w-third-forth{
  width: 75% !important;
}

.w-100{
  width: 100px !important;
}

.w-80{
  width: 80px !important;
}
</style>
